import React, { useState } from "react";
import useAdminMutation from "@/hooks/useAdminMutation";
import useAdminApi from "@/hooks/useAdminApi";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLResnapshotCard — RACKET-LAB-ROLE-4F (Feb 2026).
 *
 * Admin/staff-only console UI on top of the ROLE-4E endpoint
 *   POST /api/admin/racket-lab/operators/_ops/resnapshot-linked-users
 *
 * Lets admin/staff:
 *   1. Run a dry-run (optionally filtered by shop_id).
 *   2. Visualise the five disjoint buckets (would_update, unchanged,
 *      no_user_id, user_not_found, errors) + the rolling `applied`
 *      list once apply is triggered.
 *   3. Inspect the before/after diff for each `would_update` entry.
 *   4. Trigger apply behind a STRONG protection chain:
 *        • a fresh dry-run for the SAME selection is mandatory;
 *        • the explicit count of `would_update` is surfaced again;
 *        • a typed confirmation string `APPLY RESNAPSHOT` is required
 *          (exact match);
 *        • apply is also gated client-side to admin/staff (the backend
 *          enforces 403 independently).
 *
 * Strict invariants:
 *   • No mutation happens at mount.
 *   • No mutation happens on dry-run.
 *   • Apply only runs when ALL protections are satisfied AND the user
 *     clicks the apply button.
 *   • Roles other than admin/staff get a single "no access" notice; no
 *     bucket / no diff / no totals are rendered for them.
 *   • Emails are only surfaced inside the diff for admin/staff; the
 *     component never tries to render them for any other role.
 */
const RLResnapshotCard = () => {
  const t = useRacketLabAdminT();
  const { ctx: rlCtx } = useRLContext();
  const { run, loading } = useAdminMutation();
  const { data: shopsData } = useAdminApi("/admin/racket-lab/shops");
  const shops =
    shopsData?.items ||
    shopsData?.shops ||
    (Array.isArray(shopsData) ? shopsData : []);

  const [shopId, setShopId] = useState("");
  const [report, setReport] = useState(null);
  const [reportShopId, setReportShopId] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  // Client-side surface guard. The backend is the source of truth
  // (returns 403 to anyone else), but we hide the surface here so
  // non-admin roles never even see the totals / diff.
  const isAdminOrStaff =
    rlCtx?.profile === "admin" || rlCtx?.profile === "staff";

  if (!rlCtx) {
    return null; // loading or context not ready — render nothing.
  }
  if (!isAdminOrStaff) {
    return (
      <section
        data-testid="rl-resnapshot-card-locked"
        className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500"
      >
        {t("operators.resnapshot.locked_notice")}
      </section>
    );
  }

  const expectedConfirm = "APPLY RESNAPSHOT";
  const canApply =
    !!report &&
    report.totals?.would_update > 0 &&
    confirmText === expectedConfirm &&
    !loading;

  const onDryRun = async () => {
    const body = { dry_run: true };
    if (shopId) body.shop_id = shopId;
    const res = await run(
      "post",
      "/admin/racket-lab/operators/_ops/resnapshot-linked-users",
      body,
    ).catch(() => null);
    if (res) {
      setReport(res);
      setReportShopId(shopId || null);
      setApplyResult(null);
      setConfirmText("");
    }
  };

  const onApply = async () => {
    if (!canApply) return;
    // Re-assert selection used in the dry-run to avoid drift.
    const body = { dry_run: false };
    if (reportShopId) body.shop_id = reportShopId;
    const res = await run(
      "post",
      "/admin/racket-lab/operators/_ops/resnapshot-linked-users",
      body,
    ).catch(() => null);
    if (res) {
      setApplyResult(res);
      setReport(res); // refresh the buckets with the post-apply state.
      setConfirmText("");
    }
  };

  return (
    <section
      data-testid="rl-resnapshot-card"
      className="rounded-md border border-slate-200 bg-white p-3 text-xs"
    >
      <header className="flex items-center justify-between gap-2">
        <div>
          <h2
            data-testid="rl-resnapshot-card-title"
            className="text-sm font-semibold text-slate-900"
          >
            {t("operators.resnapshot.title")}
          </h2>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {t("operators.resnapshot.subtitle")}
          </p>
        </div>
      </header>

      {/* Filters + dry-run trigger */}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">
            {t("operators.resnapshot.shop_filter_label")}
          </span>
          <select
            data-testid="rl-resnapshot-shop-filter"
            value={shopId}
            onChange={(e) => setShopId(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1.5 text-xs"
          >
            <option value="">
              {t("operators.resnapshot.shop_filter_all")}
            </option>
            {shops.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.id}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          data-testid="rl-resnapshot-dry-run-btn"
          onClick={onDryRun}
          disabled={loading}
          className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900 disabled:bg-slate-400"
        >
          {loading
            ? t("operators.resnapshot.running")
            : t("operators.resnapshot.dry_run_btn")}
        </button>
      </div>

      {/* Report */}
      {report ? (
        <div className="mt-4 space-y-3">
          <div
            data-testid="rl-resnapshot-totals"
            className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] sm:grid-cols-7"
          >
            <Total
              testId="rl-resnapshot-totals-reviewed"
              label={t("operators.resnapshot.totals_reviewed")}
              value={report.totals?.reviewed ?? 0}
            />
            <Total
              testId="rl-resnapshot-totals-would-update"
              label={t("operators.resnapshot.totals_would_update")}
              value={report.totals?.would_update ?? 0}
              accent="text-amber-700"
            />
            <Total
              testId="rl-resnapshot-totals-unchanged"
              label={t("operators.resnapshot.totals_unchanged")}
              value={report.totals?.unchanged ?? 0}
              accent="text-emerald-700"
            />
            <Total
              testId="rl-resnapshot-totals-no-user-id"
              label={t("operators.resnapshot.totals_no_user_id")}
              value={report.totals?.no_user_id ?? 0}
            />
            <Total
              testId="rl-resnapshot-totals-user-not-found"
              label={t("operators.resnapshot.totals_user_not_found")}
              value={report.totals?.user_not_found ?? 0}
              accent="text-rose-700"
            />
            <Total
              testId="rl-resnapshot-totals-errors"
              label={t("operators.resnapshot.totals_errors")}
              value={report.totals?.errors ?? 0}
              accent={
                (report.totals?.errors ?? 0) > 0 ? "text-rose-700" : ""
              }
            />
            <Total
              testId="rl-resnapshot-totals-applied"
              label={t("operators.resnapshot.totals_applied")}
              value={report.totals?.applied ?? 0}
              accent="text-sky-700"
            />
          </div>

          {/* Diff for would_update */}
          {report.would_update && report.would_update.length > 0 ? (
            <details
              data-testid="rl-resnapshot-bucket-would-update"
              className="rounded-md border border-amber-200 bg-amber-50 p-2"
            >
              <summary className="cursor-pointer text-[11px] font-medium text-amber-800">
                {t("operators.resnapshot.bucket_would_update", {
                  count: report.would_update.length,
                })}
              </summary>
              <ul className="mt-2 space-y-2">
                {report.would_update.slice(0, 50).map((entry) => (
                  <li
                    key={entry.id}
                    data-testid={`rl-resnapshot-diff-${entry.id}`}
                    className="rounded border border-amber-100 bg-white p-2 text-[10px] text-slate-700"
                  >
                    <code className="text-slate-500">{entry.id}</code>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      <DiffSide
                        title={t("operators.resnapshot.diff_before")}
                        side={entry.before}
                      />
                      <DiffSide
                        title={t("operators.resnapshot.diff_after")}
                        side={entry.after}
                      />
                    </div>
                  </li>
                ))}
                {report.would_update.length > 50 ? (
                  <li className="text-[10px] italic text-slate-500">
                    {t("operators.resnapshot.diff_truncated", {
                      shown: 50,
                      total: report.would_update.length,
                    })}
                  </li>
                ) : null}
              </ul>
            </details>
          ) : null}

          {/* user_not_found */}
          {report.user_not_found && report.user_not_found.length > 0 ? (
            <details
              data-testid="rl-resnapshot-bucket-user-not-found"
              className="rounded-md border border-rose-200 bg-rose-50 p-2"
            >
              <summary className="cursor-pointer text-[11px] font-medium text-rose-800">
                {t("operators.resnapshot.bucket_user_not_found", {
                  count: report.user_not_found.length,
                })}
              </summary>
              <p className="mt-1 text-[10px] text-rose-700">
                {t("operators.resnapshot.bucket_user_not_found_hint")}
              </p>
              <ul className="mt-2 max-h-40 list-disc overflow-auto pl-5 text-[10px] text-slate-700">
                {report.user_not_found.slice(0, 100).map((entry) => (
                  <li key={entry.id}>
                    <code className="text-slate-500">{entry.id}</code>
                    {" · user_id="}
                    <code className="text-slate-500">{entry.user_id}</code>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          {/* Apply panel */}
          {report.totals?.would_update > 0 ? (
            <div
              data-testid="rl-resnapshot-apply-panel"
              className="rounded-md border border-slate-300 bg-slate-50 p-2"
            >
              <p className="text-[11px] text-slate-700">
                {t("operators.resnapshot.apply_explain", {
                  count: report.totals.would_update,
                })}
              </p>
              <label className="mt-2 flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wide text-slate-500">
                  {t("operators.resnapshot.apply_confirm_label", {
                    word: expectedConfirm,
                  })}
                </span>
                <input
                  data-testid="rl-resnapshot-apply-confirm-input"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={expectedConfirm}
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-xs"
                />
              </label>
              <button
                type="button"
                data-testid="rl-resnapshot-apply-btn"
                onClick={onApply}
                disabled={!canApply}
                className="mt-2 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:bg-slate-300 disabled:text-slate-500"
              >
                {t("operators.resnapshot.apply_btn")}
              </button>
              {applyResult ? (
                <p
                  data-testid="rl-resnapshot-apply-result"
                  className="mt-2 text-[10px] text-emerald-700"
                >
                  {t("operators.resnapshot.apply_result", {
                    count: applyResult.totals?.applied ?? 0,
                  })}
                </p>
              ) : null}
            </div>
          ) : (
            <p
              data-testid="rl-resnapshot-no-pending"
              className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800"
            >
              {t("operators.resnapshot.no_pending")}
            </p>
          )}
        </div>
      ) : (
        <p
          data-testid="rl-resnapshot-empty"
          className="mt-3 text-[11px] italic text-slate-500"
        >
          {t("operators.resnapshot.empty")}
        </p>
      )}
    </section>
  );
};

const Total = ({ testId, label, value, accent = "" }) => (
  <div
    data-testid={testId}
    className="flex flex-col rounded bg-white px-2 py-1.5 text-[10px]"
  >
    <span className="uppercase tracking-wide text-slate-500">{label}</span>
    <span className={`text-base font-semibold text-slate-900 ${accent}`}>
      {value}
    </span>
  </div>
);

const DiffSide = ({ title, side }) => (
  <div>
    <p className="text-[9px] uppercase tracking-wide text-slate-500">{title}</p>
    <p className="break-all">
      <code className="text-slate-600">
        {side?.linked_user_email_snapshot ?? "—"}
      </code>
    </p>
    <p className="break-all">
      <code className="text-slate-600">
        {side?.linked_user_display_name_snapshot ?? "—"}
      </code>
    </p>
  </div>
);

export default RLResnapshotCard;
